/**
 * Client-visible session presence check.
 *
 * The real session cookie (`an_session`) is `httpOnly`, so JavaScript cannot
 * read it via `document.cookie`. The CSRF cookie (`an_session_csrf`) is set and
 * cleared in lockstep with the session cookie by an-api / pkg-util-auth and is
 * intentionally NOT `httpOnly` (Axios reads it for the `x-csrf-token` header),
 * so it is the JS-visible proxy for "does a session exist right now".
 *
 * We use this to short-circuit auth flows synchronously — e.g. after logout the
 * cookie is already gone, so we can redirect to the auth portal immediately
 * instead of waiting for a `/auth/me` round-trip to come back unauthenticated.
 */
const SESSION_INDICATOR_COOKIE = process.env.CSRF_COOKIE_NAME || "an_session_csrf";

/**
 * @param name cookie name to look for
 * @returns true if a cookie with that name is present in `document.cookie`.
 */
export function hasCookie(name: string): boolean {
  if (typeof document === "undefined") return false;
  const target = `${name}=`;
  return document.cookie.split(";").some((part) => part.trim().startsWith(target));
}

/**
 * @returns true if the JS-visible session-indicator cookie is present, i.e. the
 *   user still appears to have a live session. false once it has been cleared
 *   (logout / expiry) or was never set.
 */
export function hasSessionCookie(): boolean {
  return hasCookie(SESSION_INDICATOR_COOKIE);
}
